import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';

import {
  SettingsListGroup,
  SettingsListRow,
} from '../Settings/SettingsListGroup';

export interface ConversationStarter {
  id: string;
  title: string;
  shortTitle?: string;
  description: string;
  content: string;
  preferenceId?: string;
  specTemplateId?: string;
}

interface ConversationStartersProps {
  initialStarters: ConversationStarter[];
  onSave: (starters: ConversationStarter[]) => Promise<void>;
  availablePreferences?: Array<{ id: string; label: string }>;
  availableSpecTemplates?: Array<{ id: string; label: string }>;
}

const NONE_VALUE = '__none__';

const StarterDialog = ({
  starter,
  isNew,
  onClose,
  onSave,
  availablePreferences,
  availableSpecTemplates,
}: {
  starter: ConversationStarter;
  isNew: boolean;
  onClose: () => void;
  onSave: (starter: ConversationStarter) => Promise<void>;
  availablePreferences?: Array<{ id: string; label: string }>;
  availableSpecTemplates?: Array<{ id: string; label: string }>;
}) => {
  const [draft, setDraft] = useState(starter);
  const [isSaving, setIsSaving] = useState(false);

  const update = (updates: Partial<ConversationStarter>) =>
    setDraft((prev) => ({ ...prev, ...updates }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(draft);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent size="xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>
            {isNew ? 'New Conversation Starter' : 'Edit Conversation Starter'}
          </DialogTitle>
          <DialogDescription>
            Configure a reusable prompt that appears as a starting point when
            you open Chat with a patient.
          </DialogDescription>
        </DialogHeader>
        <Stack gap={5} align="stretch">
          <Field>
            <FieldLabel size="sm">Title</FieldLabel>
            <Input
              type="text"
              placeholder="e.g. Last Visit Recap"
              value={draft.title}
              onChange={(e) => update({ title: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel size="sm">Short Title</FieldLabel>
            <Input
              type="text"
              placeholder="e.g. Recap"
              value={draft.shortTitle ?? ''}
              onChange={(e) =>
                update({ shortTitle: e.target.value || undefined })
              }
            />
          </Field>
          <Field>
            <FieldLabel size="sm">Description</FieldLabel>
            <Input
              type="text"
              placeholder="One-line summary shown below the title"
              value={draft.description}
              onChange={(e) => update({ description: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel size="sm">Instructions</FieldLabel>
            <Textarea
              placeholder="What should Chat do when this starter is selected?"
              value={draft.content}
              onChange={(e) => update({ content: e.target.value })}
              rows={4}
            />
          </Field>
          {availablePreferences && availablePreferences.length > 0 && (
            <Field>
              <FieldLabel size="sm">Output Preference</FieldLabel>
              <Select
                value={draft.preferenceId ?? NONE_VALUE}
                onValueChange={(value) =>
                  update({
                    preferenceId: value === NONE_VALUE ? undefined : value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>None</SelectItem>
                  {availablePreferences.map((pref) => (
                    <SelectItem key={pref.id} value={pref.id}>
                      {pref.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {availableSpecTemplates && availableSpecTemplates.length > 0 && (
            <Field>
              <FieldLabel size="sm">Spec Template</FieldLabel>
              <Select
                value={draft.specTemplateId ?? NONE_VALUE}
                onValueChange={(value) =>
                  update({
                    specTemplateId: value === NONE_VALUE ? undefined : value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>None</SelectItem>
                  {availableSpecTemplates.map((tmpl) => (
                    <SelectItem key={tmpl.id} value={tmpl.id}>
                      {tmpl.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </Stack>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={!draft.title || !draft.description || isSaving}
            loading={isSaving}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SortableStarterRow = ({
  starter,
  onEdit,
  onDelete,
  availablePreferences,
  availableSpecTemplates,
}: {
  starter: ConversationStarter;
  onEdit: () => void;
  onDelete: () => void;
  availablePreferences?: Array<{ id: string; label: string }>;
  availableSpecTemplates?: Array<{ id: string; label: string }>;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: starter.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <SettingsListRow
        leading={
          <button
            type="button"
            aria-label={`Reorder ${starter.title}`}
            className="cursor-grab touch-none text-text-tertiary hover:text-text-secondary"
            {...attributes}
            {...listeners}
          >
            <Icon name="GripVertical" size="lg" />
          </button>
        }
        editLabel={`Edit ${starter.title}`}
        deleteLabel={`Delete ${starter.title}`}
        onEdit={onEdit}
        onDelete={onDelete}
      >
        <Text size="sm" weight="semibold" color="primary">
          {starter.title}
        </Text>
        {starter.description && (
          <Text size="xs" weight="medium" color="tertiary">
            {starter.description}
          </Text>
        )}
        {starter.preferenceId && availablePreferences && (
          <Text size="xs" color="tertiary">
            Preference:{' '}
            {availablePreferences.find((p) => p.id === starter.preferenceId)
              ?.label ?? 'Unknown'}
          </Text>
        )}
        {starter.specTemplateId && availableSpecTemplates && (
          <Text size="xs" color="tertiary">
            Template:{' '}
            {availableSpecTemplates.find((t) => t.id === starter.specTemplateId)
              ?.label ?? 'Unknown'}
          </Text>
        )}
      </SettingsListRow>
    </div>
  );
};

/**
 * Managed list of conversation starters: rows with drag-to-reorder and
 * hover-revealed edit/delete, plus a create/edit dialog. Every change is
 * persisted immediately via `onSave` with the full updated list.
 */
export const ConversationStarters = ({
  initialStarters,
  onSave,
  availablePreferences,
  availableSpecTemplates,
}: ConversationStartersProps) => {
  const toast = useToast();

  const [starters, setStarters] = useState(initialStarters);
  const [editing, setEditing] = useState<{
    starter: ConversationStarter;
    isNew: boolean;
  } | null>(null);

  useEffect(() => {
    setStarters(initialStarters);
  }, [initialStarters]);

  const persist = async (next: ConversationStarter[]) => {
    const previous = starters;
    setStarters(next);
    try {
      await onSave(next);
      return true;
    } catch {
      setStarters(previous);
      toast.error('Failed to save conversation starters');
      return false;
    }
  };

  const handleCreate = () => {
    setEditing({
      starter: {
        id: crypto.randomUUID(),
        title: '',
        description: '',
        content: '',
      },
      isNew: true,
    });
  };

  const handleDialogSave = async (starter: ConversationStarter) => {
    const exists = starters.some((s) => s.id === starter.id);
    const next = exists
      ? starters.map((s) => (s.id === starter.id ? starter : s))
      : [...starters, starter];
    const saved = await persist(next);
    if (saved) {
      setEditing(null);
    }
  };

  const handleDelete = (id: string) => {
    void persist(starters.filter((s) => s.id !== id));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = starters.findIndex((item) => item.id === active.id);
      const newIndex = starters.findIndex((item) => item.id === over.id);
      void persist(arrayMove(starters, oldIndex, newIndex));
    }
  };

  return (
    <>
      <SettingsListGroup
        countLabel={`${starters.length} ${starters.length === 1 ? 'starter' : 'starters'}`}
        actions={
          <Button variant="outline" size="sm" onClick={handleCreate}>
            Create New
          </Button>
        }
      >
        {starters.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={starters.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {starters.map((starter, i) => (
                <div key={starter.id}>
                  <SortableStarterRow
                    starter={starter}
                    onEdit={() => setEditing({ starter, isNew: false })}
                    onDelete={() => handleDelete(starter.id)}
                    availablePreferences={availablePreferences}
                    availableSpecTemplates={availableSpecTemplates}
                  />
                  {i < starters.length - 1 && <Separator className="my-0" />}
                </div>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Text size="sm" color="tertiary" className="px-6 py-7">
            No conversation starters configured
          </Text>
        )}
      </SettingsListGroup>

      {editing && (
        <StarterDialog
          starter={editing.starter}
          isNew={editing.isNew}
          onClose={() => setEditing(null)}
          onSave={handleDialogSave}
          availablePreferences={availablePreferences}
          availableSpecTemplates={availableSpecTemplates}
        />
      )}
    </>
  );
};
